import Vue from "vue";
import {
    DocumentSignature,
    DocumentSaveSignatureRequest,
    FetchState,
    SigningDocument,
    SigningDocumentState,
    SignedDocumentKey,
    SignedDocumentKeyFetchStateDictionary,
    DrivewealthAccountManagementType,
    SigningDocumentContentType,
    LeafHouseDisclosureDocumentKey,
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as DrivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import {
    CustodianDefaultAgreementModel,
    EmployerAgreementModel,
    UpdateCustodianDefaultAgreementRequest,
    UpdateEmployerAgreementRequest
} from "@/admin/types";

export const initialState: SigningDocumentState = {
    allDocumentsAdminFetchState: FetchState.Uninitialized,
    documentsAdmin: [],
    documentFetchState: FetchState.Uninitialized,
    document: null,
    signatureFetchState: FetchState.Uninitialized,
    signature: null,
    signedDocumentKeys: [],
    signedDocumentKeyFetchState: {},
    custodianDefaultAgreements: [],
    custodianDefaultAgreementFetchState: FetchState.Uninitialized,
    employerAgreements: [],
    EmployerAgreementFetchState: FetchState.Uninitialized,
    hsabDrivewealthAccountSignatureFetchState: FetchState.Uninitialized,
    hsabDrivewealthAccountSignature: null,
    leafHouseDocumentSignaturesFetchState: FetchState.Uninitialized,
    leafHouseDocumentSignatures: []
};

export const useSigningDocumentStore = defineStore("signingDocument", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        leafHouseAccountSignatureLoaded (): boolean {
            return this.leafHouseDocumentSignaturesFetchState === FetchState.Loaded;
        },
        hsabDrivewealthAccountSignatureLoaded (): boolean {
            return this.hsabDrivewealthAccountSignatureFetchState === FetchState.Loaded;
        },
        hsabDrivewealthAccountSignatureIsSigned (): boolean {
            return this.hsabDrivewealthAccountSignatureFetchState === FetchState.Loaded &&
                this.hsabDrivewealthAccountSignature !== null;
        },
        isDocumentLoaded (): boolean {
            return this.documentFetchState === FetchState.Loaded;
        },
        allDocumentsLoadedAdmin (): boolean {
            return this.allDocumentsAdminFetchState === FetchState.Loaded;
        },
        currentDocument (): SigningDocument {
            return this.document;
        },
        isSigned (): boolean {
            return this.signatureFetchState === FetchState.Loaded && this.signature !== null;
        },
        signedKeys (): SignedDocumentKey[] {
            if (this.signedDocumentKeys) {
                return this.signedDocumentKeys;
            }

            return [];
        },
        signedKey () {
            return accountType => {
                const signedDocument = _.find(this.signedDocumentKeys, x => x.accountType === accountType);

                if (signedDocument) {
                    return signedDocument;
                }

                return null;
            };
        },
        leafHouseDocumentSignatureByDocumentKey () {
            return documentKey => {
                const signedDocument = _.find(this.leafHouseDocumentSignatures, x => x.documentKey === documentKey);

                if (signedDocument) {
                    return signedDocument;
                }

                return null;
            };
        },
        hasLeafHouseDocumentSignatures () {
          return this.leafHouseDocumentSignatures.length > 0;
        },
        drivewealthAccountSignatureDocumentKey (): string {
            return "hsabank_investment_esign";
        }
    },
    actions: {
        setDocumentFetchState (fetchState: FetchState): void {
            this.documentFetchState = fetchState;
        },
        setLeafHouseDocumentSignaturesFetchState (fetchState: FetchState): void {
            this.leafHouseDocumentSignaturesFetchState = fetchState;
        },
        setSignatureFetchState (fetchState: FetchState): void {
            this.signatureFetchState = fetchState;
        },
        setHsabDrivewealthAccountSignatureFetchState (fetchState: FetchState): void {
            this.hsabDrivewealthAccountSignatureFetchState = fetchState;
        },
        setAllDocumentsAdminFetchState (fetchState: FetchState): void {
            this.allDocumentsAdminFetchState = fetchState;
        },
        setRecordsAdmin (documentsAdmin: SigningDocument[]): void {
            this.documentsAdmin = [ ...documentsAdmin ];
        },
        setCurrentDocument (document: SigningDocument): void {
            this.document = document;
        },
        setSignature (signature: DocumentSignature): void {
            this.signature = signature;
        },
        setHsabDrivewealthAccountSignature (signature: DocumentSignature): void {
            this.hsabDrivewealthAccountSignature = signature;
        },
        setSignedKeys (signedDocumentKey: SignedDocumentKey): void {
            this.signedDocumentKeys = [...this.signedDocumentKeys, signedDocumentKey];
        },
        setLeafHouseDocumentSignatures (documentSignature: DocumentSignature): void {
            this.leafHouseDocumentSignatures = [...this.leafHouseDocumentSignatures, documentSignature];
        },
        removeLeafHouseDocumentSignatures (): void {
            this.leafHouseDocumentSignatures = [];
        },
        removeSignedKey (accountType: DrivewealthAccountManagementType): void {
            const filteredSignedDocumentKeys = _.filter(this.signedDocumentKeys, x => x.accountType !== accountType);

            this.signedDocumentKeys = [...filteredSignedDocumentKeys];
            this.setSignedKeyFetchState({
                accountType,
                fetchState: FetchState.Uninitialized
            });
        },
        removeSignedKeyByKey (documentKey: string): void {
            const filteredSignedDocumentKeys = _.filter(this.signedDocumentKeys, x => x.documentKey !== documentKey);

            this.signedDocumentKeys = [...filteredSignedDocumentKeys];
        },
        setSignedKeyFetchState (signedDocumentKeyFetchState: SignedDocumentKeyFetchStateDictionary): void {
            Vue.set(this.signedDocumentKeyFetchState,
                signedDocumentKeyFetchState.accountType,
                signedDocumentKeyFetchState);
        },
        setCustodianDefaultAgreement (custodianDefaultAgreements: CustodianDefaultAgreementModel[]): void {
            this.custodianDefaultAgreements = custodianDefaultAgreements;
        },
        setCustodianDefaultAgreementFetchState (fetchState: FetchState): void {
            this.custodianDefaultAgreementFetchState = fetchState;
        },
        setEmployerAgreement (employerAgreements: EmployerAgreementModel[]): void {
            this.employerAgreements = employerAgreements;
        },
        setEmployerAgreementFetchState (fetchState: FetchState): void {
            this.EmployerAgreementFetchState = fetchState;
        },
        deleteSignature (key: string): Promise<void> {
            return new Promise<void>(resolve => UserService.deleteSignature(key)
                .then(_ => {
                    this.setSignature(null);
                    resolve();
                })
                .catch(e => storeHelper.handleError({ error: e, rethrow: true }))
            );
        },
        markDeleteSignature (key: string): Promise<void> {
            return new Promise<void>(resolve => UserService.markDeleteSignature(key)
                .then(_ => {
                    this.setSignature(null);
                    resolve();
                })
                .catch(e => storeHelper.handleError({ error: e, rethrow: true }))
            );
        },
        get ({ documentKey, version }): Promise<void> {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getSigningDocument(documentKey, version).then(resp => {
                this.setCurrentDocument(resp.data);
                this.setDocumentFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getByIdAdmin ({ documentId, version }): Promise<void> {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getSigningDocumentByIdAdmin(documentId, version).then(resp => {
                this.setCurrentDocument(resp.data);
                this.setDocumentFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getDocumentLatestVersion (documentKey): Promise<void> {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getDocumentLatestVersion(documentKey)
                .then(r => this.setCurrentDocument(r.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }))
                .finally(() => this.setDocumentFetchState(FetchState.Loaded));
        },
        getDocumentLatestVersionAdmin (documentKey): Promise<void> {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getDocumentLatestVersionAdmin(documentKey)
                .then(r => this.setCurrentDocument(r.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }))
                .finally(() => this.setDocumentFetchState(FetchState.Loaded));
        },
        getSignature (documentKey): Promise<void> {
            this.setSignatureFetchState(FetchState.Loading);
            return UserService.getSignature(documentKey).then(resp => {
                this.setSignature(resp.data);
                this.setSignatureFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getSignedDocumentKey (accountType: DrivewealthAccountManagementType): Promise<void> {
            const signedDocument = _.find(this.signedDocumentKeyFetchState, x => x.accountType === accountType);
            if (!accountType || (signedDocument && signedDocument.fetchState !== FetchState.Uninitialized)) {
                return;
            }
            this.setSignedKeyFetchState({
                accountType,
                fetchState: FetchState.Loading
            });
            return UserService.getUserSignedDocumentKey(accountType).then(resp => {
                this.setSignedKeys({
                    accountType,
                    documentKey: resp.data.documentKey,
                    pathToFile: resp.data.pathToFile
                });
                this.setSignedKeyFetchState({
                    accountType,
                    fetchState: FetchState.Loaded
                });
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getHsabDrivewealthAccountSignature (): Promise<void> {
            this.setHsabDrivewealthAccountSignatureFetchState(FetchState.Loading);
            return UserService.getSignature(this.drivewealthAccountSignatureDocumentKey).then(resp => {
                this.setHsabDrivewealthAccountSignature(resp.data);
                this.setHsabDrivewealthAccountSignatureFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getLeafhouseAccountSignatureDocuments (options: {
            force?: boolean,
        }): Promise<any> {
            if (!options.force && this.leafHouseDocumentSignaturesFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setLeafHouseDocumentSignaturesFetchState(FetchState.Loading);
            const signaturePromises = Object.keys(LeafHouseDisclosureDocumentKey).map((key: string) => {
                const documentKey = LeafHouseDisclosureDocumentKey[key];
                return UserService.getSignature(documentKey).then(resp => {
                    if (resp.data) {
                        this.setLeafHouseDocumentSignatures(resp.data);
                    }
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            });

            return Promise.allSettled([...signaturePromises]).then(() => {
                this.setLeafHouseDocumentSignaturesFetchState(FetchState.Loaded);
            });
        },
        saveSignature (request: DocumentSaveSignatureRequest): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                return UserService.saveSignature(request).then(resp => {
                    const notifierStore = useNotifierStore();
                    if (!request.disableNotification) {
                        notifierStore.setSuccess(`Document signed.`);
                    }
                    resolve();
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        save (document: SigningDocument, file: any): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                if (this.documentFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setDocumentFetchState(FetchState.Loading);
                    const notifierStore = useNotifierStore();

                    let promise = null;

                    if (document.contentType === SigningDocumentContentType.Pdf) {
                        promise = UserService.saveFile(document, file);
                    } else {
                        promise = UserService.saveSigningDocument(document);
                    }

                    return promise.then(resp => {
                        this.setDocumentFetchState(FetchState.Loaded);
                        notifierStore.setSuccess(`Document has been saved.`);
                        resolve();
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        loadAllAdmin (): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                if (this.allDocumentsAdminFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setAllDocumentsAdminFetchState(FetchState.Loading);
                    return UserService.getSigningDocuments().then(resp => {
                        this.setRecordsAdmin(resp.data);
                        this.setAllDocumentsAdminFetchState(FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        getCustodianDefaultAgreement (custodian: number): Promise<void> {
            this.setCustodianDefaultAgreementFetchState(FetchState.Loading);
            return DrivewealthService.getCustodianDefaultAgreement(custodian).then(resp => {
                this.setCustodianDefaultAgreement(resp.data);
                this.setCustodianDefaultAgreementFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        saveCustodianDefaultAgreement (request: UpdateCustodianDefaultAgreementRequest): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                return DrivewealthService.saveCustodianDefaultAgreement(request).then(resp => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess(`Default agreement has been saved.`);
                    resolve();
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        getEmployerAgreement (employerId: number): Promise<void> {
            this.setEmployerAgreementFetchState(FetchState.Loading);
            return DrivewealthService.getEmployerAgreement(employerId).then(resp => {
                this.setEmployerAgreement(resp.data);
                this.setEmployerAgreementFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        saveEmployerAgreement (request: UpdateEmployerAgreementRequest): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                return DrivewealthService.saveEmployerAgreement(request).then(resp => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess(`Default agreement has been saved.`);
                    resolve();
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        }
    }
});